/**
 * @deprecated This component needs refactoring to use the new form feature paradigm.
 * It currently uses inline FormField rendering instead of the shared FormField component.
 * TODO: Refactor to use FormField from @themes/pollinator/features/form/FormField or create a
 * controlled version in @themes/pollinator/features/form/controllers/
 */
import { useController, useFormContext } from "react-hook-form";
import { Search } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Label } from "@themes/pollinator/ui/Label";
import { FieldError } from "@themes/pollinator/ui/FieldError";
import { Select, OptionType } from "@themes/pollinator/ui/Select";

export type Props = {
  /** Form field name */
  name: string;
  /** Available options */
  options: OptionType[];
  /** Loading state */
  isLoading: boolean;
  /** Controlled value */
  value?: OptionType | null;
  /** Selection handler */
  onSelect?: (val: OptionType | null) => void;
  /** Create handler for new options */
  onCreate?: (input: string) => void;
  /** Additional CSS classes */
  className?: string;
};

/**
 * Organization lookup input with search and create functionality.
 */
export function OrgLookupInput({
  name,
  options,
  isLoading,
  value,
  onSelect,
  onCreate,
  className,
}: Props) {
  const { control } = useFormContext();

  const {
    field: { value: rhfValue, onChange },
    fieldState: { error },
    formState,
  } = useController({
    name,
    control,
    defaultValue: null,
  });

  const useControlled =
    typeof value !== "undefined" && typeof onSelect === "function";
  const effectiveValue = useControlled ? value : rhfValue;

  const handleChange = (selected: OptionType | OptionType[] | null) => {
    // This component only supports single select, so extract single value
    const singleValue = Array.isArray(selected)
      ? selected[0] ?? null
      : selected;
    if (useControlled) {
      onSelect?.(singleValue);
    } else {
      onChange(singleValue);
    }
  };

  return (
    <div
      className={twMerge(
        "relative flex flex-col gap-1 transition-opacity duration-100 p-1",
        formState.isSubmitting && "opacity-50",
        className
      )}
      data-testid="org-lookup-input"
    >
      <Label htmlFor={name} id={`${name}-label`} error={!!error}>
        Select or enter any program you are affiliated with
      </Label>
      <div className="relative flex w-full items-center">
        <span className="absolute left-3 top-3 flex items-center w-5 h-5 text-brand-700 z-10">
          <Search className="w-5 h-5" data-testid="org-lookup-input-search-icon" />
        </span>
        <div className="flex flex-col relative w-full gap-1.5">
          <Select
            name={name}
            isCreatable
            options={options}
            isLoading={isLoading}
            isWithIcon
            placeholder="School, residency, or fellowship."
            value={
              options.find(
                (o) =>
                  o.value === effectiveValue?.value ||
                  o.value === effectiveValue
              ) ??
              (typeof effectiveValue === "string" && effectiveValue
                ? { label: effectiveValue, value: effectiveValue }
                : effectiveValue || null)
            }
            onChange={handleChange}
            onCreate={onCreate}
          />
          {error && <FieldError id={`${name}-error`} message={error.message} />}
        </div>
      </div>
    </div>
  );
}

export default OrgLookupInput;

